import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import Landing from "./pages/Landing";
import { Login, Register } from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Module from "./pages/Module";
import Results from "./pages/Results";
import Payment, { PaymentSuccess } from "./pages/Payment";
import Plan90 from "./pages/Plan90";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import CGV from "./pages/CGV";
import AdminPromo from "./pages/AdminPromo";
import AdminUsers from "./pages/AdminUsers";
import AdminFunnel from "./pages/AdminFunnel";
import QuickQuiz from "./pages/QuickQuiz";
import MiniResults from "./pages/MiniResults";
import ErrorBoundary from "./components/ErrorBoundary";
import InstallPWA from "./components/InstallPWA";
import "./index.css";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="flex items-center gap-3 text-stone-600">
          <div className="w-6 h-6 border-2 border-stone-300 border-t-[#FF6B6B] rounded-full animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="flex items-center gap-3 text-stone-600">
          <div className="w-6 h-6 border-2 border-stone-300 border-t-[#FF6B6B] rounded-full animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Landing />} />
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/cgv" element={<CGV />} />
      <Route 
        path="/quick-quiz" 
        element={<ProtectedRoute><QuickQuiz /></ProtectedRoute>} 
      />
      <Route 
        path="/mini-results" 
        element={<ProtectedRoute><MiniResults /></ProtectedRoute>} 
      />
      <Route 
        path="/admin/promo" 
        element={
          <ProtectedRoute>
            <AdminPromo />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/users" 
        element={
          <ProtectedRoute>
            <AdminUsers />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/funnel" 
        element={
          <ProtectedRoute>
            <AdminFunnel />
          </ProtectedRoute>
        } 
      />
      
      {/* Protected routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/module/:moduleId" 
        element={
          <ProtectedRoute>
            <Module />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/results" 
        element={
          <ProtectedRoute>
            <Results />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/payment" 
        element={
          <ProtectedRoute>
            <Payment />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/payment/success" 
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/plan" 
        element={
          <ProtectedRoute>
            <Plan90 />
          </ProtectedRoute>
        } 
      />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <ErrorBoundaryWrapper>
            <div className="App">
              <AppRoutes />
              <InstallPWAWrapper />
              <Toaster 
                position="top-center" 
                richColors 
                toastOptions={{
                  style: {
                    fontFamily: 'Manrope, sans-serif',
                  },
                }}
              />
              {/* Subtle grain overlay for texture */}
              <div className="grain-overlay" />
            </div>
          </ErrorBoundaryWrapper>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

// Wrapper to pass language to ErrorBoundary
function ErrorBoundaryWrapper({ children }) {
  const { language } = useLanguage();
  return <ErrorBoundary language={language}>{children}</ErrorBoundary>;
}

// Wrapper to access language context
function InstallPWAWrapper() {
  const { language } = useLanguage();
  return <InstallPWA language={language} />;
}

export default App;
